import type { LocationQueryValue } from 'vue-router';

import { baseRequestClient, requestClient } from '#/api/request';

export namespace AuthApi {
  /** 登录接口参数 */
  export interface LoginParams {
    password?: string;
    username?: string;
  }

  /** 登录接口返回值 */
  export interface LoginResult {
    accessToken?: string;
    forceRedirect?: boolean;
    redirectReason?: string;
    redirectSubdomainPrefix?: string;
    token?: string;
  }

  export interface RefreshTokenResult {
    data: string;
    status: number;
  }

  export interface DistributorRegistrationSettings {
    customerServiceContact?: string;
    distributorId?: number;
    distributorSubjectId?: number;
    inviteCode?: string;
    inviteRegisterUrl?: string;
    loginBgImageUrl?: string; // AI-Generated by Claude · 分销商多域名登录
    loginDisplayName?: string;
    loginLogoUrl?: string;
    loginSubtitle?: string;
    subdomainPrefix?: string;
    templateType?: string; // AI-Generated by Claude · 分销商多域名登录 T6
    templateUrl?: string; // AI-Generated by Claude · 分销商多域名登录 T6
    themeColor?: string; // AI-Generated by Claude · 分销商多域名登录 商户端主题色
  }
}

/**
 * 登录
 */
export async function loginApi(data: AuthApi.LoginParams) {
  return requestClient.post<AuthApi.LoginResult>('/auth/login', data);
}

/**
 * 刷新accessToken
 */
export async function refreshTokenApi() {
  return baseRequestClient.post<AuthApi.RefreshTokenResult>('/auth/refresh', {
    withCredentials: true,
  });
}

/**
 * 退出登录
 */
export async function logoutApi() {
  return baseRequestClient.post('/merchant/logout', {
    withCredentials: true,
  });
}

/**
 * 获取用户权限码
 */
export async function getAccessCodesApi() {
  return requestClient.get<string[]>('/auth/codes');
}

export async function recognizeDistributorBrandBySubdomainApi(
  subdomainPrefix: string,
) {
  return requestClient.get<AuthApi.DistributorRegistrationSettings>(
    '/distributor-registration/recognize/subdomain',
    {
      params: { subdomainPrefix },
      headers: {
        isToken: false,
      },
      notNeedErrorTipCode: 500,
    },
  );
}

export async function loginSmsCodeApi(data: AuthApi.LoginParams) {
  return requestClient.post<AuthApi.LoginResult>('/merchant/sms-login', data);
}

export async function loginPhoneApi(data: AuthApi.LoginParams) {
  return requestClient.post<AuthApi.LoginResult>('/merchant/phone-login', data);
}

export async function distributorLoginSmsCodeApi(data: AuthApi.LoginParams) {
  return requestClient.post<AuthApi.LoginResult>(
    '/distributor/sms-login',
    data,
  );
}

export async function distributorLoginPhoneApi(data: AuthApi.LoginParams) {
  return requestClient.post<AuthApi.LoginResult>(
    '/distributor/phone-login',
    data,
  );
}

// 获取字典列表
export async function getDictionaryListApi() {
  return requestClient.get('/merchant/find-dictionary-list');
}

// 获取店铺列表
export async function getShopListApi(data: Record<string, any>) {
  return requestClient.post(`/merchant/shop/shop-list`, data);
}

// 解除授权店铺
export async function unbindShopListApi(data: Record<string, any>) {
  return requestClient.post(`/merchant/shop/unbind`, data);
}

// 维护店铺别称：传 null 或空串清除别称
export async function updateShopAliasApi(
  shopId: number,
  shopAlias?: null | string,
) {
  return requestClient.post(`/merchant/shop/${shopId}/alias`, { shopAlias });
}

// 授权店铺
export async function shopAuthApi({
  code,
  error,
  state,
}: {
  code?: LocationQueryValue[] | string;
  error?: LocationQueryValue[] | string;
  state?: LocationQueryValue[] | string;
}) {
  return requestClient.get(`/merchant/shop/auth`, {
    params: { code, error, state },
    timeout: 100_000,
    notNeedErrorTipCode: 500,
  });
}

// 批量刷新已授权店铺token
export async function shopRefreshTokenByMerchantApi() {
  return requestClient.post(
    `/merchant/shop/refresh-token-by-merchant`,
    {},
    { timeout: 100_000 },
  );
}

// 获取授权链接
export async function shopAuthLinkApi(
  shopType: 'CROSS_BORDER' | 'US_LOCAL',
  shopId?: number,
) {
  return requestClient.get(`/merchant/shop/auth-link`, {
    params: { shopId, shopType },
  });
}

export async function retryShopOnboardingApi(shopId: number) {
  return requestClient.post(`/merchant/shop/onboarding/retry`, { shopId });
}
